/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';

import { withTheme } from 'styled-components';

import {
  IChartingLibraryWidget,
  ResolutionString,
  TradingTerminalWidgetOptions,
} from 'charting_library_trading_platform/charting_library';
import useTheme from 'hooks/useTheme';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppSelector } from 'storeHooks';

import { widget } from '../../charting_library_trading_platform';
import { TvStyle } from './chart_style';
import fundingTVDatafeed from './fundingTVDatafeed';

const isCI = process.env.REACT_APP_ENV === 'ci';

const prependAppToUrl = url => (isCI ? `/${url}` : `/app/${url}`);

const chartConfig = ({ symbol, themeObj, activeTheme }): TradingTerminalWidgetOptions => {
  let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone && timezone.toLowerCase() === 'asia/calcutta') {
    timezone = 'Asia/Kolkata';
  }

  return {
    symbol,
    interval: '240' as ResolutionString,
    container: 'funding-chart-container',
    datafeed: fundingTVDatafeed(),
    library_path: prependAppToUrl('charting_library_trading_platform/'),
    custom_css_url: prependAppToUrl(
      `charting_library_trading_platform/customStyles.css?v=${Date.now()}`
    ),
    locale: 'en',
    disabled_features: [
      'remove_library_container_border',
      'header_undo_redo',
      'header_saveload',
      'border_around_the_chart',
      'symbol_search_hot_key',
      'header_symbol_search',
      'display_market_status',
      'header_widget',
      'volume_force_overlay',
      'create_volume_indicator_by_default',
      'show_object_tree',
      'trading_account_manager',
      'right_toolbar',
      'display_market_status',
    ],
    enabled_features: [
      'hide_last_na_study_output',
      'dont_show_boolean_study_arguments',
      'side_toolbar_in_fullscreen_mode',
      'chart_template_storage',
      'hide_object_tree_and_price_scale_exchange_label',
      'auto_enable_symbol_labels',
      'adaptive_logo',
      'header_compare',
      'header_screenshot',
      'show_symbol_logo_in_legend',
      'show_symbol_logos',
      'symbol_info',
      'horz_touch_drag_scroll',
      'iframe_loading_compatibility_mode',
    ],
    autosize: true,
    timezone,
    theme: activeTheme,
    client_id: 'delta.exchange',
    favorites: {
      chartTypes: ['Line'],
    },
    loading_screen: {
      backgroundColor: themeObj.primaryBackground,
      foregroundColor: themeObj.primaryTheme,
    },
    height: '100%',
    overrides: {
      // 'paneProperties.backgroundType': 'solid',
      'paneProperties.background': themeObj.primaryBackground,
      'paneProperties.vertGridProperties.color': themeObj.separator,
      'paneProperties.horzGridProperties.color': themeObj.separator,
      'paneProperties.vertGridProperties.style': 1,
      'paneProperties.horzGridProperties.style': 1,
      'paneProperties.bottomMargin': 5,

      // Select chart type
      'mainSeriesProperties.style': 1,
      'mainSeriesProperties.showCountdown': true,
      // Bar style
      'mainSeriesProperties.barStyle.upColor': themeObj.positive,
      'mainSeriesProperties.barStyle.downColor': themeObj.negative,
      'mainSeriesProperties.barStyle.barColorsOnPrevClose': false,
      'mainSeriesProperties.barStyle.dontDrawOpen': true,
      // Candle Style
      'mainSeriesProperties.candleStyle.upColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.downColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.drawWick': true,
      'mainSeriesProperties.candleStyle.wickUpColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.wickDownColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.barColorsOnPrevClose': false,
      // Hollow Candle Style
      'mainSeriesProperties.hollowCandleStyle.upColor': themeObj.positive,
      'mainSeriesProperties.hollowCandleStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.hollowCandleStyle.downColor': themeObj.negative,
      'mainSeriesProperties.hollowCandleStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.hollowCandleStyle.drawWick': false,
      // Heikin Ashi styles
      'mainSeriesProperties.haStyle.upColor': themeObj.positive,
      'mainSeriesProperties.haStyle.downColor': themeObj.negative,
      'mainSeriesProperties.haStyle.drawWick': true,
      'mainSeriesProperties.haStyle.drawBorder': true,
      'mainSeriesProperties.haStyle.borderColor': '',
      'mainSeriesProperties.haStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.haStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.haStyle.wickUpColor': themeObj.positive,
      'mainSeriesProperties.haStyle.wickDownColor': themeObj.negative,
      'mainSeriesProperties.haStyle.barColorsOnPrevClose': false,
      // Area Style
      'mainSeriesProperties.areaStyle.color1': themeObj.positive,
      'mainSeriesProperties.areaStyle.color2': themeObj.negative,
      'mainSeriesProperties.areaStyle.linecolor': themeObj.negative,
      // Line styles
      'mainSeriesProperties.lineStyle.color': themeObj.negative,
      'mainSeriesProperties.lineStyle.linestyle': 0,
      'mainSeriesProperties.lineStyle.linewidth': 1,
      // Baseline styles
      'mainSeriesProperties.baselineStyle.baselineColor': themeObj.primaryText,
      'mainSeriesProperties.baselineStyle.topFillColor1': themeObj.positive,
      'mainSeriesProperties.baselineStyle.topFillColor2': 'rgba( 83, 185, 135, 0.1)',
      'mainSeriesProperties.baselineStyle.bottomFillColor1': themeObj.negative,
      'mainSeriesProperties.baselineStyle.bottomFillColor2': 'rgba( 235, 77, 92, 0.1)',
      'mainSeriesProperties.baselineStyle.topLineColor': themeObj.positive,
      'mainSeriesProperties.baselineStyle.bottomLineColor': themeObj.negative,
      // Legend properties
      'paneProperties.legendProperties.showSeriesTitle': false,
      'paneProperties.legendProperties.showSeriesOHLC': true,
      'paneProperties.legendProperties.showStudyTitles': true,
      'paneProperties.legendProperties.showStudyValues': true,
      'scalesProperties.fontSize': 12,
      'scalesProperties.showSymbolLabels': false,
      'scalesProperties.showStudyLastValue': true,
      'symbolWatermarkProperties.color': 'rgba(110, 110, 110, 0.1)',
      'scalesProperties.lineColor': themeObj.separator,
      'scalesProperties.textColor': themeObj.secondaryText,
      'paneProperties.crossHairProperties.color': themeObj.secondaryText,
      'mainSeriesProperties.statusViewStyle.symbolTextSource': 'ticker',
      'paneProperties.topMargin': 0,
    },
    studies_overrides: {
      'volume.volume.color.0': themeObj.negative,
      'volume.volume.color.1': themeObj.positive,
      'volume.precision': 2,
    },
  };
};

// why theme is not used here?
const FundingTvContainer = (props: { theme: any }) => {
  const { theme } = props;
  const selectedProduct = useAppSelector(selectedProductSelector);
  const [chartReady, setChartReady] = useState(false);
  const fundingTvWidgetRef = useRef<null | IChartingLibraryWidget>(null);
  const { isDarkTheme, chartBackgroundColor } = useTheme();

  const initiateCharts = useCallback(() => {
    const symbol = `FUNDING:${selectedProduct?.symbol ?? 'BTCUSDT'}`;
    const widgetOptions = chartConfig({
      symbol,
      themeObj: theme,
      activeTheme: isDarkTheme ? 'dark' : 'light',
    });

    // eslint-disable-next-line new-cap
    const tvWidget: IChartingLibraryWidget | null = new widget(widgetOptions);

    tvWidget?.onChartReady(() => {
      fundingTvWidgetRef.current = tvWidget;
      setChartReady(true);

      tvWidget?.activeChart()?.setChartType(2);
      tvWidget?.applyOverrides({
        // udpates background color of the chart
        'paneProperties.backgroundGradientStartColor': chartBackgroundColor,
        'paneProperties.backgroundGradientEndColor': chartBackgroundColor,
        // adding it here since the some of the properties in charts config are not reflecting
        'mainSeriesProperties.lineStyle.color': theme.negative,
        'mainSeriesProperties.candleStyle.wickUpColor': theme.positive,
        'mainSeriesProperties.candleStyle.wickDownColor': theme.negative,
        'mainSeriesProperties.candleStyle.upColor': theme.positive,
        'mainSeriesProperties.candleStyle.borderUpColor': theme.positive,
        'mainSeriesProperties.candleStyle.downColor': theme.negative,
        'mainSeriesProperties.candleStyle.borderDownColor': theme.negative,
      });
    });
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      initiateCharts();
    }

    return () => {
      if (chartReady && fundingTvWidgetRef?.current) {
        setChartReady(false);
        fundingTvWidgetRef?.current?.remove();
        fundingTvWidgetRef.current = null;
      }
    };
  }, [isDarkTheme, selectedProduct]);

  return <TvStyle id="funding-chart-container" className="tv_chart_container" />;
};

export default withTheme(FundingTvContainer);
